import { useState } from 'react';
import { cn } from '../../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../styling/stylingHelper';
import {
  combineDuplicateProducts,
  ignoreDuplicateProducts,
  type DuplicateScanResult,
  type DuplicateProductGroup,
} from '../../../../../services/admin/adminService';

/** Stable identity for a scanned group, used to remove it from the list after combine/ignore. */
function duplicateGroupKey(g: DuplicateProductGroup): string {
  return `${g.restaurantId}|${g.platform}|${g.products.map(p => p.productName).join('§')}`;
}

export function DuplicateProductsView({ result, setResult }: { result: DuplicateScanResult; setResult: React.Dispatch<React.SetStateAction<DuplicateScanResult | null>> }) {
  const [restaurantFilter, setRestaurantFilter] = useState('all');

  const restaurantOptions = Array.from(new Map(result.groups.map(g => [g.restaurantId, g.restaurantName])))
    .sort((a, b) => a[1].localeCompare(b[1]));
  // Fall back to 'all' when the selected restaurant no longer has groups
  // (e.g. its last group was combined/ignored).
  const effectiveFilter = restaurantOptions.some(([id]) => id === restaurantFilter) ? restaurantFilter : 'all';
  const filteredGroups = effectiveFilter === 'all'
    ? result.groups
    : result.groups.filter(g => g.restaurantId === effectiveFilter);

  const removeGroup = (key: string) => {
    setResult(prev => prev ? { ...prev, groups: prev.groups.filter(g => duplicateGroupKey(g) !== key) } : prev);
  };

  return (
    <div className="space-y-4">
      {/* Summary + restaurant filter */}
      <div className="flex items-center justify-between gap-3">
        <p className={cn(primaryText, smallTextSize)}>
          Scanned <span className={semiBoldText}>{result.totalProductsScanned}</span> products across{' '}
          <span className={semiBoldText}>{result.restaurantsScanned}</span> restaurants — found{' '}
          <span className={cn(semiBoldText, result.groups.length > 0 ? 'text-orange-600 dark:text-orange-400' : '')}>{result.groups.length}</span> duplicate group(s)
          {effectiveFilter !== 'all' && <span className={secondaryText}> ({filteredGroups.length} shown)</span>}
        </p>
        <select
          value={effectiveFilter}
          onChange={(e) => setRestaurantFilter(e.target.value)}
          className={cn(
            'px-3 py-2 rounded-lg border',
            'border-gray-300 dark:border-gray-600',
            'bg-white dark:bg-gray-700',
            primaryText, smallTextSize
          )}
        >
          <option value="all">All restaurants</option>
          {restaurantOptions.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>
      </div>

      {filteredGroups.length === 0 && (
        <div className={cn(secondaryText, smallTextSize, 'text-center py-8')}>
          No duplicate groups found.
        </div>
      )}

      {filteredGroups.map(group => (
        <DuplicateGroupRow key={duplicateGroupKey(group)} group={group} onDone={() => removeGroup(duplicateGroupKey(group))} />
      ))}
    </div>
  );
}

function DuplicateGroupRow({ group, onDone }: { group: DuplicateProductGroup; onDone: () => void }) {
  // Default canonical = variant with the most order lines (sorted first by the server).
  const [canonicalName, setCanonicalName] = useState(group.products[0]?.productName ?? '');
  // Variants included in the combine — all by default; unchecking lets the admin
  // exclude a wrongly-grouped product without ignoring the whole group.
  const [included, setIncluded] = useState<Set<string>>(new Set(group.products.map(p => p.productName)));
  const [busy, setBusy] = useState<'combine' | 'ignore' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggleIncluded = (name: string) => {
    setIncluded(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      // Keep the canonical choice valid: if it was just excluded, move it to
      // the first remaining included variant.
      if (!next.has(canonicalName)) {
        const first = group.products.find(p => next.has(p.productName));
        setCanonicalName(first?.productName ?? '');
      }
      return next;
    });
  };

  const handleCombine = async () => {
    const variants = group.products
      .map(p => p.productName)
      .filter(n => included.has(n) && n !== canonicalName);
    if (!canonicalName || !included.has(canonicalName) || variants.length === 0) return;
    if (!window.confirm(`Combine ${variants.length} variant(s) into "${canonicalName}"?\n\nThis renames all order history, cost price, inventory and price alert references, and rewrites future invoices.`)) return;
    try {
      setBusy('combine');
      setError(null);
      await combineDuplicateProducts(group.restaurantId, group.platform, canonicalName, variants);
      onDone();
    } catch (err: any) {
      setError(err.message || 'Failed to combine duplicates');
      setBusy(null);
    }
  };

  const handleIgnore = async () => {
    try {
      setBusy('ignore');
      setError(null);
      await ignoreDuplicateProducts(group.restaurantId, group.platform, group.products.map(p => p.productName));
      onDone();
    } catch (err: any) {
      setError(err.message || 'Failed to ignore duplicates');
      setBusy(null);
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className={cn(primaryText, smallTextSize)}>
          <span className={semiBoldText}>{group.restaurantName}</span>
          <span className={cn(secondaryText, 'mx-2')}>·</span>
          <span className="font-mono">{group.platform}</span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleIgnore}
            disabled={busy !== null}
            className={cn(
              'px-3 py-1.5 rounded-lg transition-colors',
              'bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              primaryText, smallTextSize
            )}
          >
            {busy === 'ignore' ? 'Ignoring...' : 'Ignore'}
          </button>
          <button
            onClick={handleCombine}
            disabled={busy !== null || included.size < 2}
            className={cn(
              'px-3 py-1.5 rounded-lg transition-colors',
              'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700 text-white dark:text-white',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              smallTextSize, semiBoldText
            )}
          >
            {busy === 'combine' ? 'Combining...' : `Combine${included.size < group.products.length ? ` (${included.size})` : ''}`}
          </button>
        </div>
      </div>

      <div className="space-y-1">
        {group.products.map(p => (
          <div
            key={p.productName}
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
              !included.has(p.productName)
                ? 'opacity-50 border border-transparent'
                : canonicalName === p.productName
                  ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 border border-transparent'
            )}
          >
            <input
              type="checkbox"
              checked={included.has(p.productName)}
              onChange={() => toggleIncluded(p.productName)}
              className="accent-purple-600"
              title="Include in combine"
            />
            <input
              type="radio"
              name={`canonical-${duplicateGroupKey(group)}`}
              checked={canonicalName === p.productName}
              disabled={!included.has(p.productName)}
              onChange={() => setCanonicalName(p.productName)}
              className="accent-purple-600"
              title="Surviving name"
            />
            <span className={cn(primaryText, smallTextSize, semiBoldText, 'flex-1 truncate')} title={p.productName}>
              {p.productName}
            </span>
            <span className={cn(secondaryText, 'text-xs font-mono whitespace-nowrap')}>
              {p.lineCount} line{p.lineCount !== 1 ? 's' : ''}
              <span className="mx-1">·</span>
              {p.latestPrice != null ? `€${p.latestPrice.toFixed(2)}` : '—'}
              <span className="mx-1">·</span>
              last {new Date(p.lastOrderDate).toLocaleDateString('nl-NL')}
            </span>
          </div>
        ))}
      </div>
      <p className={cn(secondaryText, 'text-xs mt-2')}>
        Radio = surviving name; checkbox = include in combine (excluded products are left untouched and resurface on the next scan).
      </p>

      {error && (
        <div className="mt-2 p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs">
          {error}
        </div>
      )}
    </div>
  );
}
